import React, { useEffect } from "react";
import { useContext, useState } from "react";
import {
	BusinessDataContext,
	BusinessDataContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";
import {
	filterSchedulesByAnticipation,
	getServiceAnticipation,
} from "../../../utils/filter";
import { LangContext } from "../../../types/internationalization";
import { formatTime } from "../../../utils/dateFormat";
import { MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useWindowResize } from "../../../hooks/useWindowResize";
import { styles } from "./FindTable";
import { MenuProps } from "./DinersSelect";
import dayjs from "dayjs";

export const TimeSelect: React.FC<{
	schedules: string[];
	disabled: boolean;
}> = ({ schedules, disabled }) => {
	const langContext = useContext(LangContext);

	const { isXSmall, isMobile } = useWindowResize();

	const { reservation, onChangeDate, onChangeTime } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const { services } = useContext(
		BusinessDataContext
	) as BusinessDataContextValues;

	// Declare a list of schedules in case we need to filter.
	// If no service is selected, generate schedules
	// from the first available time to the last in 30-minute intervals.
	const [filteredSchedules, setFilteredSchedules] =
		useState<string[]>(schedules);

	const [selectedTime, setSelectedTime] = useState<string>(
		reservation.schedule
			? formatTime(reservation.schedule, langContext?.locale!)
			: ""
	);

	//If the language is changed, I will update the selected time to the current language
	useEffect(() => {
		if (selectedTime !== "") {
			setSelectedTime(formatTime(selectedTime, langContext?.locale!));
		} else {
			setSelectedTime("");
		}
		// eslint-disable-next-line
	}, [langContext]);

	// If the schedules list changes, we filter the schedules by anticipation
	useEffect(() => {
		if (reservation.time) {
			let filtered = schedules;
			let anticipation = getServiceAnticipation(reservation, services);

			// Filter schedules to display based on the required anticipation
			filtered = filterSchedulesByAnticipation(
				reservation.time,
				filtered,
				anticipation
			);

			if (filtered.length > 0) {
				setFilteredSchedules(filtered);
				// I only set the selectedTime the first filtered schedule
				// if the selected time is not in the filtered schedules
				if (!filtered.includes(formatTime(selectedTime, "es-ES"))) {
					setSelectedTime(formatTime(filtered[0], langContext?.locale!));
					onChangeTime(filtered[0]);
					//But if it is, and the reservation schedule don't, it means that the user has changed language
				} else if (reservation.schedule !== selectedTime) {
					const time = formatTime(selectedTime, "es-ES");
					onChangeTime(time);
				}
			} else {
				// If no schedules are available, move to the next day
				const selectedDate = reservation.time;
				selectedDate.setDate(selectedDate.getDate() + 1);
				selectedDate.setHours(0, 0, 0, 0);
				onChangeDate(dayjs(selectedDate));
			}
		}
		// eslint-disable-next-line
	}, [schedules]);

	const handleChange = (event: SelectChangeEvent) => {
		let time = event.target.value as string;
		console.log({ time });
		setSelectedTime(time);
		time = formatTime(time, "es-ES");
		console.log({ time });
		onChangeTime(time);
	};

	return (
		<Select
			className="select timeSelect"
			disabled={disabled}
			displayEmpty
			value={selectedTime}
			variant="outlined"
			IconComponent={ExpandMoreIcon}
			onChange={handleChange}
			sx={
				isXSmall || isMobile ? styles.mediumSelectMobile : styles.mediumSelect
			}
			MenuProps={MenuProps}
			renderValue={(selected) => {
				if (selected === "") {
					return (
						<Typography fontWeight={700}>
							{filteredSchedules.length > 0 &&
								formatTime(filteredSchedules[0], langContext?.locale!)}
						</Typography>
					);
				}
				return <Typography fontWeight={700}>{selected}</Typography>;
			}}
			inputProps={{ "aria-label": "Without label" }}
		>
			{filteredSchedules.map((time, index) => {
				const formattedTime = formatTime(time, langContext?.locale!);
				return (
					<MenuItem value={formattedTime} key={index} sx={{ fontWeight: 700 }}>
						{formattedTime}
					</MenuItem>
				);
			})}
		</Select>
	);
};
